import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const PARTNER_KEYS = [
  "whoWeWork.p1",
  "whoWeWork.p2",
  "whoWeWork.p3",
  "whoWeWork.p4",
  "whoWeWork.p5",
  "whoWeWork.p6",
];

export default function WhoWeWorkWith() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="bg-navy py-20 md:py-28" ref={ref}>
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div
            className={`transition-all duration-700 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <p className="text-xs uppercase tracking-widest text-gold font-medium mb-4">
              {t("whoWeWork.label")}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-white leading-snug mb-6">
              {t("whoWeWork.title")}
            </h2>
            <hr className="border-white/10 mb-6 w-16" />
            <p className="text-white/65 text-base leading-relaxed mb-8">
              {t("whoWeWork.p")}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="#contact"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-gold text-white font-semibold text-sm rounded-md hover:bg-gold-light transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                {t("whoWeWork.cta")}
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-base" />
                </span>
              </a>
              <Link
                to="/partner-registration"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-white/10 text-white font-semibold text-sm rounded-md border border-white/30 hover:bg-white/20 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-team-line text-base" />
                </span>
                Become a Partner
              </Link>
            </div>
          </div>

          <div
            className={`transition-all duration-700 delay-200 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <p className="text-xs uppercase tracking-widest text-gold/80 font-medium mb-6">
              {t("whoWeWork.supportLabel")}
            </p>
            <ul className="space-y-4">
              {PARTNER_KEYS.map((key, i) => (
                <li
                  key={key}
                  className={`flex items-center gap-4 transition-all duration-500 ${
                    visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-6"
                  }`}
                  style={{ transitionDelay: `${i * 80 + 300}ms` }}
                >
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-checkbox-circle-line text-gold text-lg" />
                  </span>
                  <span className="text-white/80 text-sm">{t(key)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
